import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';

type Mode = 'signin' | 'signup' | 'confirm' | 'forgot' | 'forgot-confirm';

const COPY: Record<Mode, { eyebrowKey: string; titleKey: string }> = {
  signin: { eyebrowKey: 'login.welcomeBackEyebrow', titleKey: 'login.welcomeBackTitle' },
  signup: { eyebrowKey: 'login.beginEyebrow', titleKey: 'login.beginTitle' },
  confirm: { eyebrowKey: 'login.almostThereEyebrow', titleKey: 'login.almostThereTitle' },
  forgot: { eyebrowKey: 'login.resetEyebrow', titleKey: 'login.resetTitle' },
  'forgot-confirm': { eyebrowKey: 'login.resetEyebrow', titleKey: 'login.newPasswordTitle' },
};

export function Login() {
  const { signIn, signUp, confirmSignUp, resendCode, forgotPassword, confirmForgotPassword, googleSignIn } = useAuth();
  const { t } = useTranslation();

  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const goTo = (next: Mode) => {
    setMode(next);
    setError(null);
    setNotice(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setIsSubmitting(true);
    try {
      if (mode === 'signin') {
        await signIn(email, password);
      } else if (mode === 'signup') {
        await signUp(email, password);
        setMode('confirm');
        setNotice(t('login.codeSent'));
      } else if (mode === 'confirm') {
        await confirmSignUp(email, code);
        setNotice(t('login.emailConfirmed'));
        setMode('signin');
      } else if (mode === 'forgot') {
        await forgotPassword(email);
        setMode('forgot-confirm');
        setNotice(t('login.codeSent'));
      } else if (mode === 'forgot-confirm') {
        await confirmForgotPassword(email, code, newPassword);
        setNotice(t('login.passwordReset'));
        setMode('signin');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common.somethingWentWrong'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    setNotice(null);
    try {
      await resendCode(email);
      setNotice(t('login.codeResent'));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common.somethingWentWrong'));
    }
  };

  const { eyebrowKey, titleKey } = COPY[mode];

  return (
    <div className="login-layout">
      <div className="login-panel">
        <div className="login-form-wrap">
          <span className="login-brand">
            <span className="logo-badge">
              <img src="/logo-face.png" alt="" className="logo-face" />
            </span>
            <span className="wordmark">Omnist</span>
          </span>

          <p className="login-eyebrow">{t(eyebrowKey)}</p>
          <h1 className="login-title wordmark">{t(titleKey)}</h1>

          {mode === 'signin' && (
            <>
              <button type="button" className="google-btn" onClick={googleSignIn}>
                <GoogleIcon /> {t('login.continueWithGoogle')}
              </button>
              <div className="login-divider"><span>{t('login.or')}</span></div>
            </>
          )}

          <form className="login-form" onSubmit={handleSubmit} noValidate>
            {(mode === 'signin' || mode === 'signup' || mode === 'forgot') && (
              <label className="login-field">
                <span className="login-label">{t('login.email')}</span>
                <input
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="login-input"
                />
              </label>
            )}

            {(mode === 'confirm' || mode === 'forgot-confirm') && (
              <label className="login-field">
                <span className="login-label">{t('login.code')}</span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="login-input"
                />
              </label>
            )}

            {(mode === 'signin' || mode === 'signup') && (
              <label className="login-field">
                <span className="login-label">{t('login.password')}</span>
                <input
                  type="password"
                  autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="login-input"
                />
              </label>
            )}

            {mode === 'forgot-confirm' && (
              <label className="login-field">
                <span className="login-label">{t('login.newPassword')}</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="login-input"
                />
              </label>
            )}

            {mode === 'signin' && (
              <button type="button" className="login-link" onClick={() => goTo('forgot')}>
                {t('login.forgotPassword')}
              </button>
            )}
            {mode === 'confirm' && (
              <button type="button" className="login-link" onClick={handleResend}>
                {t('login.resendCode')}
              </button>
            )}

            {error && <p className="login-error">{error}</p>}
            {notice && !error && <p className="login-notice">{notice}</p>}

            <button type="submit" className="btn btn-primary login-submit" disabled={isSubmitting}>
              {isSubmitting ? t('common.pleaseWait') : t(SUBMIT_LABEL_KEY[mode])}
            </button>
          </form>

          <p className="login-switch">
            {mode === 'signin' && (
              <>{t('login.needAccount')} <button type="button" className="login-link" onClick={() => goTo('signup')}>{t('login.signUp')}</button></>
            )}
            {(mode === 'signup' || mode === 'confirm' || mode === 'forgot' || mode === 'forgot-confirm') && (
              <>{t('login.alreadyHaveAccount')} <button type="button" className="login-link" onClick={() => goTo('signin')}>{t('login.signIn')}</button></>
            )}
          </p>
        </div>
      </div>

      <div className="login-hero-panel">
        <img src="/login-hero.jpg" alt="" className="login-hero-image" />
        <p className="login-tagline wordmark">{t('login.tagline')}</p>
      </div>
    </div>
  );
}

const SUBMIT_LABEL_KEY: Record<Mode, string> = {
  signin: 'login.signIn',
  signup: 'login.createAccount',
  confirm: 'login.confirmEmail',
  forgot: 'login.sendCode',
  'forgot-confirm': 'login.resetPassword',
};

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.28-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.05l3.01-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}
